// @flow
// $FlowFixMe
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router';
import { FormattedMessage, injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';
import classnames from 'classnames';
import Swiper from 'react-id-swiper';
import { shuffle } from 'lodash';
import { commonMessages } from '../messages/Common';
import styles from './Facts.css';
import IconLabel from '../util/IconLabel';
import * as analyticsActions from '../../actions/analytics';
import * as uiActions from '../../actions/ui';
import CapacitorRipple from '../capacitor/Ripple';
import { selectUserIsAuthenticated } from '../../selectors/user';
import { thisMessages } from '../messages/BookletPreviewsModal';
import { selectLocale } from '../../selectors/client';
import { splitLocale } from '../../lib/locale-utils';

declare var __CAPACITOR__: boolean;
declare var __ELECTRON__: boolean;

type Fact = {
  topic: {
    id: string,
    description: string,
  },
  fact: string,
  source: {
    id: string,
    description: string,
  },
};

type OwnProps = {
  facts: Array<Fact>,
  bookletUrl?: string,
  imageUrl?: string,
  entityType: string,
  entityId: string,
  screen: string,
};

type MapStateToProps = {
  isAuthenticated: boolean,
  language: string,
};

type DispatchProps = {
  track: Function,
  showModal: Function,
};

type Props = OwnProps & MapStateToProps & DispatchProps & { intl: IntlShape };

const Facts = ({
  facts,
  imageUrl,
  language,
  bookletUrl,
  entityType,
  entityId,
  screen,
  intl,
  track,
  showModal,
  isAuthenticated,
}: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const swiperRef = useRef(null);
  const containerRef = useRef(null);

  const shuffledFacts = useMemo(() => shuffle(facts), [facts]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasBeenViewed(true);
          observer.disconnect();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!hasBeenViewed || !shuffledFacts.length) return;
    const fact = shuffledFacts[currentIndex];
    track('Viewed Fact', {
      contextType: entityType,
      contextId: entityId,
      topic: fact.topic.id,
      index: currentIndex,
      screen,
    });
  }, [hasBeenViewed, currentIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!facts || facts.length === 0) {
    return null;
  }

  const totalFacts = shuffledFacts.length;

  const goToPrevious = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slidePrev();

      if (currentIndex === 0) {
        swiperRef.current.swiper.slideTo(totalFacts - 1);
      }
    }
  };

  const goToNext = () => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideNext();

      if (currentIndex === totalFacts - 1) {
        swiperRef.current.swiper.slideTo(0);
      }
    }
  };

  const swiperParams = {
    slidesPerView: 1,
    keyboard: totalFacts > 1,
    on: {
      slideChange: function handleSlideChange() {
        setCurrentIndex(this.realIndex);
      },
    },
    noSwiping: totalFacts <= 1,
  };

  const trackSelectedFact = (fact: Fact, index: number) => {
    track('Selected Fact', {
      contextType: entityType,
      contextId: entityId,
      topic: fact.topic.id,
      index,
      screen,
    });
  };

  const ReadMore = () => {
    return (
      <React.Fragment>
        <FormattedMessage id="interesting-facts.read-more" defaultMessage="Read more" />
        <IconLabel name="arrow-up-right" size="small" />
      </React.Fragment>
    );
  };

  const openBooklet = (fact: Fact, index: number, e: SyntheticEvent<HTMLButtonElement>) => {
    trackSelectedFact(fact, index);

    if (!isAuthenticated) {
      showModal(
        'FREE_EXPERIENCE_PREVIEWS_MODAL',
        { trigger: 'booklet' },
        {
          imageUrl,
          trigger: 'bookletPreviewsModal',
          title: (
            <FormattedMessage
              id="booklet-previews-modal.header"
              defaultMessage="Want to see this booklet?"
            />
          ),
          subtitle: (
            <FormattedMessage
              id="booklet-previews-modal.subtitle"
              defaultMessage="Create a free account to read all about {resourceName}"
              values={{ resourceName: intl.formatMessage(thisMessages[entityType]) }}
            />
          ),
        }
      );
      return;
    }

    if (__ELECTRON__) {
      window.require('electron').ipcRenderer.send('view-booklet', bookletUrl, language);
      e.preventDefault();
      return;
    }
    showModal('BOOKLET_MODAL', { trigger: entityType }, { bookletUrl });
  };

  const OpenBookletButton = ({ fact, index }: { fact: Fact, index: number }) => {
    if (!bookletUrl) {
      return null;
    }

    if (__CAPACITOR__) {
      return (
        <Link
          to={`/booklet?bookletUrl=${bookletUrl}`}
          onClick={() => trackSelectedFact(fact, index)}
          className={styles.readMoreButton}
        >
          <CapacitorRipple />
          <ReadMore />
        </Link>
      );
    }
    return (
      <button
        type="button"
        className={styles.readMoreButton}
        onClick={e => openBooklet(fact, index, e)}
      >
        <ReadMore />
      </button>
    );
  };

  return (
    <div className={styles.container} ref={containerRef} id="facts">
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>
          <FormattedMessage id="interesting-facts.title" defaultMessage="Did you know?" />
        </h2>
        {totalFacts > 1 && (
          <span className={styles.counter}>
            {currentIndex + 1}/{totalFacts}
          </span>
        )}
      </div>

      <div className={styles.card}>
        {totalFacts > 1 && (
          <button
            type="button"
            className={classnames(styles.navButton, styles.navButtonLeft)}
            onClick={goToPrevious}
            aria-label={intl.formatMessage(commonMessages.previous)}
          >
            <CapacitorRipple />
            <IconLabel name="chevron-left" />
          </button>
        )}

        <div className={styles.contentWrapper}>
          {totalFacts > 1 && (
            <div className={styles.progressContainer}>
              {shuffledFacts.map((_, index) => (
                <div
                  key={index}
                  className={classnames(
                    styles.progressLine,
                    index === currentIndex && styles.progressLineActive,
                    index < currentIndex && styles.progressLineCompleted
                  )}
                />
              ))}
            </div>
          )}

          <Swiper {...swiperParams} ref={swiperRef}>
            {shuffledFacts.map((fact, index) => (
              <div key={index}>
                <div className={styles.slideContent}>
                  <h3 className={styles.topic}>{fact.topic.description}</h3>
                  <p className={styles.factText}>{fact.fact}</p>
                  {totalFacts > 1 && (
                    <React.Fragment>
                      <button
                        className={styles.tapAreaLeft}
                        onClick={goToPrevious}
                        aria-label={intl.formatMessage(commonMessages.previous)}
                      />
                      <button
                        className={styles.tapAreaRight}
                        onClick={goToNext}
                        aria-label={intl.formatMessage(commonMessages.next)}
                      />
                    </React.Fragment>
                  )}
                </div>
                <div className={styles.footer}>
                  {fact.source && (
                    <span className={styles.source}>
                      <FormattedMessage
                        id="interesting-facts.source"
                        defaultMessage="Source: {source}"
                        values={{ source: fact.source.description }}
                      />
                    </span>
                  )}
                  {fact.source && fact.source.id === 'booklet' && (
                    <OpenBookletButton fact={fact} index={index} />
                  )}
                </div>
              </div>
            ))}
          </Swiper>
        </div>

        {totalFacts > 1 && (
          <button
            type="button"
            className={classnames(styles.navButton, styles.navButtonRight)}
            onClick={goToNext}
            aria-label={intl.formatMessage(commonMessages.next)}
          >
            <CapacitorRipple />
            <IconLabel name="chevron-right" />
          </button>
        )}
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  const locale = selectLocale(state);
  return {
    isAuthenticated: selectUserIsAuthenticated(state),
    language: splitLocale(locale).language,
  };
}

const dispatchProps = {
  track: analyticsActions.track,
  showModal: uiActions.showModal,
};

export default connect(mapStateToProps, dispatchProps)(injectIntl(Facts));
